import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

import PaginatedTable from './PaginatedTable';
import '../css/PointComponent.css';

mapboxgl.accessToken = 'pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA';

const PointComponent = ({ vectorTilesUrl = "https://demo.flowlogic.com/tile/data/point" }) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const geocoderContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [featureData, setFeatureData] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [favoriteZipcodes, setFavoriteZipcodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeMetric, setActiveMetric] = useState('roi');
  const [metricControl, setMetricControl] = useState(null);
  const [legendControl, setLegendControl] = useState(null);
  
  const [propertyList, setPropertyList] = useState([]);
  const [originalPropertyList, setOriginalPropertyList] = useState([]); // Store original (unfiltered) property list
  const [styleLoaded, setStyleLoaded] = useState(false); // Flag to track style load status
  const authToken = localStorage.getItem('authToken');
  const [roiFilter, setRoiFilter] = useState('All'); // State for ROI filter
  const [appreciationFilter, setAppreciationFilter] = useState('All'); // State for Appreciation filter
  const [scoreFilter, setScoreFilter] = useState('All'); // State for Score filter
  
  const roiColors = {
    roi: [
        { color: '#acacac', label: '0' },
        { color: '#FFE36B', label: '5' },
        { color: '#FFB36B', label: '10' },
        { color: '#FE5C5C', label: '15' },
        { color: '#F26BFF', label: '20' },
        { color: '#CE1E50', label: '25' },
    ],
    appreciation: [
        { color: '#acacac', label: 'All' },
        { color: '#FFE36B', label: '0' },
        { color: '#FFB36B', label: '5' },
        { color: '#FE5C5C', label: '10' },
        { color: '#F26BFF', label: '15' },
        { color: '#CE1E50', label: '20' },
       
    ],
    score: [
        { color: '#acacac', label: '0' },
        { color: '#FFE36B', label: '7' },
        { color: '#FFB36B', label: '10' },
        { color: '#FE5C5C', label: '15' },
        { color: '#F26BFF', label: '20' },
        { color: '#CE1E50', label: '25' },
       
    ],
};
 
  function updateCircleColor(metric) {
    if (!roiColors[metric]) return;
  
    const colorStops = ['step', ['get', metric], ...roiColors[metric]];
  
    map.setPaintProperty('point-layer', 'circle-color', colorStops);
  }
  // Fetch favorite zipcodes
  useEffect(() => {
    fetch("https://beta.flowlogic.com/zip/favorite/all/", {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(res => res.json())
      .then(data => setFavoriteZipcodes(data.favourites || []))
      .catch(err => console.error("Failed to load favorites", err));
  }, [authToken]);

  // Initialize map and geocoder
  useEffect(() => {
    if (!mapContainer.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-95.7129, 37.0902],
      zoom: 3,
    });

    mapRef.current = map;

    // Setup geocoder and mount to container
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      countries: 'us',
      types: 'region,district,postcode,place',
      placeholder: 'Search location...',
      mapboxgl: mapboxgl,
    });

    if (geocoderContainerRef.current) {
      geocoderContainerRef.current.innerHTML = '';
      geocoder.addTo(geocoderContainerRef.current);
    }

    geocoder.on('result', (e) => {
      const coords = e.result.center;
      map.flyTo({ center: coords, zoom: 10 });
    });

    map.on('load', () => {
      map.addSource('vector-tiles', {
        type: 'vector',
        tiles: ["https://demo.flowlogic.com/tilesgl/data/point/{z}/{x}/{y}.pbf"],
      });

      map.addLayer({
        id: 'point-layer',
        type: 'circle',
        source: 'vector-tiles',
        'source-layer': 'mapplotting',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            0, 6,
            4, 8,
            6, 10,
            8, 20,
            10, 24,
          ],
          'circle-color': [
            // 'step', ['get', 'roi'],
            'step', 
            ['get', activeMetric],
            '#acacac', 
            0, '#acacac',   
            5, '#FFE36B',
            10, '#FFB36B',
            15, '#FE5C5C',
            20, '#F26BFF',
            25, '#CE1E50',
          ],
        },
        layout: {
          // This is the key part
          'circle-sort-key': ['get', 'roi'], // Higher ROI = drawn later = on top
        }
      });
    });

    map.on('sourcedata', (e) => {
      if (e.sourceId === 'vector-tiles' && e.isSourceLoaded) {
        fetchFeatureData();
      }
    });

    map.on('moveend', fetchFeatureData);

    map.on('click', 'point-layer', (e) => {
      const feature = e.features[0];
      if (!feature) return;

      const { roi, appreciation, score, zipcode, city, state } = feature.properties;

      const popupContent = `
        <div class="tooltipOuter">
          <div class="tooltipTitle">
            <span>${zipcode}</span>
            <span class="grey">${city}, ${state}</span>
          </div>
          <div class="tooltipContentOuter">
            <div class="tooltipContent">
              <div class="tooltipValues"><strong>ROI</strong> <span>${roi}%</span></div>
              <div class="tooltipValues"><strong>Appreciation</strong> <span>${appreciation}%</span></div>
              <div class="tooltipValues"><strong>FLI</strong> <span>${score}</span></div>
            </div>
            <div class="tooltipViewBtn">
              <button id="viewMoreBtn">
                <span>Explore</span>
                <img src="/assets/arw-grey.svg" alt="FlowLogic Icon" />
              </button>
            </div>
          </div>
        </div>
      `;

      new mapboxgl.Popup({ offset: 15, closeButton: true })
        .setLngLat(e.lngLat)
        .setHTML(popupContent)
        .addTo(map);
    });
    setMap(map);
    map.on('style.load', () => {
      setStyleLoaded(true); // Set the flag to true when the style is loaded
     });
    return () => map.remove();
  }, []);

  //map draing according to the filters
  // useEffect(() => {
  //   if (!mapRef.current || !mapRef.current.getLayer('point-layer')) return;

  //   if (activeTab === 'favorites' && favoriteZipcodes.length > 0) {
  //     mapRef.current.setFilter('point-layer', ['in', ['get', 'zipcode'], ['literal', favoriteZipcodes]]);
  //   } else {
  //     mapRef.current.setFilter('point-layer', null);
  //   }
  // }, [activeTab, favoriteZipcodes]);

  useEffect(() => {
    if (!mapRef.current || !mapRef.current.getLayer('point-layer')) return;
  
    const filters = ['all'];
  
    if (activeTab === 'favorites' && favoriteZipcodes.length > 0) {
      filters.push(['in', ['get', 'zipcode'], ['literal', favoriteZipcodes]]);
    }
  
    if (roiFilter !== 'All') {
      filters.push(['>=', ['get', 'roi'], roiFilter]);
    }
  
    if (appreciationFilter !== 'All') {
      filters.push(['>=', ['get', 'appreciation'], appreciationFilter]);
    }
  
    if (scoreFilter !== 'All') {
      filters.push(['>=', ['get', 'score'], scoreFilter]);
    }
  
    mapRef.current.setFilter('point-layer', filters.length > 1 ? filters : null);
  }, [activeTab, favoriteZipcodes, roiFilter, appreciationFilter, scoreFilter]);
  
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.getSource('collisions')) return;
  
    const geojsonData = {
      type: 'FeatureCollection',
      features: propertyList.map(property => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(property.longitude), parseFloat(property.latitude)]
        },
        properties: {
          ...property
        }
      }))
    };
  
    // Update source data
    mapRef.current.getSource('collisions').setData(geojsonData);
  }, [propertyList]);
  
//update Datas

const updateFilter = (stateName = '') => {

  console.log('Enter updateFilter',stateName);
  if (!styleLoaded || !map) {
      console.log('Map style is not loaded yet');
      return;
  }

  // setPopupInstance(null);
  // if (popupInstance) {
  //     popupInstance.remove(); // Remove the previous popup
  //     setPopupInstance(null);
  //     console.log("after drag small popup",popupInstance);
  // }
  // Ensure originalPropertyList is available
  if (originalPropertyList && originalPropertyList.length > 0) {
      // Function to filter properties by current map bounds and active metric filter
      if (!map || !styleLoaded) {
          console.log('Map instance or style is not loaded yet');
          return;
      }

      
      const filterProperties = () => {
          // Get current map bounds
          const bounds = map.getBounds();
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
           //console.log(sw);
           //console.log(ne);
          // console.log(searchPincode);
          // Apply the ROI or Appreciation filter and map bounds filtering

          // Step 1: Filter properties based on the active tab (Favorites or All)
          console.log("tab",activeTab);

          ///working script for favorites
          const filteredByTab = activeTab === 'favorites'
          ? originalPropertyList.filter(property => property.favorite) // Only favorites
          : originalPropertyList; // All properties
          console.log(originalPropertyList);
         // const filteredProperties = originalPropertyList.filter((property) => {
          const filteredProperties = filteredByTab.filter((property) => {
              const lat = parseFloat(property.latitude);
              const lng = parseFloat(property.longitude);
              const propertyZipcode =property.zipcode;
              console.log(lat);
              // Ensure valid coordinates
              if (isNaN(lat) || isNaN(lng)) return false;

      
              // If no zipcode filter, apply map bounds and ROI filter
             // const isWithinBounds = lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng;
            
               // Apply map bounds only if NOT in favorites tab
              const isWithinBounds = activeTab === 'favorites'
              ? true
              : lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng;

              // Apply ROI, Appreciation, and Score filters
             
             const meetsROIFilter = (roiFilter === 'All') || (property.roi >= roiFilter);
              //const meetsAppreciationFilter = appreciationFilter ? property.appreciation >= appreciationFilter : true;
              const meetsAppreciationFilter = (appreciationFilter === 'All') || (property.appreciation >= appreciationFilter);
              //const meetsScoreFilter = scoreFilter ? property.score >= scoreFilter : true;
              const meetsScoreFilter = (scoreFilter === 'All') || (property.score >= scoreFilter);
              //const meetsSalesFilter = (property.sales >= minSales && property.sales <= maxSales);
      
             // return isWithinBounds && meetsROIFilter;
             return isWithinBounds && meetsROIFilter && meetsAppreciationFilter && meetsScoreFilter;
          });

          

          return filteredProperties;
      };

      // Get the filtered properties based on the bounds and active filter
      const filteredProperties = filterProperties();
      // Apply a filter to the map layer
      
      
      

      // // Sort the filtered properties
      // const sortedProperties = sortProperties(filteredProperties, sortKey, sortOrder);
      // // Update the propertyList state with filtered properties
      setPropertyList(filteredProperties);
      // setPropertyList(sortedProperties);
      // setIsLoading(false);
      // Remove existing layers and sources if they exist
      if (map.getLayer('collisions')) {
          map.removeLayer('collisions');
      }
      if (map.getLayer('roi-labels')) {
          map.removeLayer('roi-labels');
      }
      if (map.getSource('collisions')) {
          map.removeSource('collisions');
      }

      // Reuse setMapData to update the map with filtered properties
     // setMapData(map, sortByROI(filteredProperties)); // Update map with filtered properties
  } else {
      console.log('No properties available to filter.');
  }
};

useEffect(() => {
    if (map) {
        console.log("Enter map");
        // Bind to map movement (zoom or pan)
        map.on('moveend', () => {
            console.log("movened");
            updateFilter(); // Filter properties based on the current map bounds
        });
       
        // Bind to zoom events (optional, but useful for map interaction)
        // map.on('zoomend', () => {
        //     console.log("movenzoomed");
        //     updateFilter(); // Reapply filters after zoom
        // });

        // Call updateFilter initially to apply filter based on the initial map state
        updateFilter();
    }
}, [roiFilter, appreciationFilter, scoreFilter, activeMetric,originalPropertyList]);


//update circle color when change activemetric
  function updateCircleColor(metric) {
    if (map && map.getLayer('point-layer')) {
      map.setPaintProperty('point-layer', 'circle-color', [
        'step',
        ['get', metric],
        '#acacac', 
        0, '#acacac',   
        5, '#FFE36B',
        10, '#FFB36B',
        15, '#FE5C5C',
        20, '#F26BFF',
        25, '#CE1E50',
      ]);
    }
  }
  useEffect(() => {
    if (map) {
        // If a MetricToggleButton control exists, remove it
        if (metricControl) {
            map.removeControl(metricControl);
        }
        if (legendControl) {
          map.removeControl(legendControl);
      }
        // Create and add the new MetricToggleButton control
        const newToggleButtonControl = new MetricToggleButton(setActiveMetric, activeMetric);
        map.addControl(newToggleButtonControl, 'top');

        // Update the button text when activeMetric changes
        newToggleButtonControl.updateButtonText(activeMetric);

        // Store the new control instance to track it for future updates
        setMetricControl(newToggleButtonControl);
        
        updateCircleColor(activeMetric);

       // const control = new ColorPalette1(isFullWidth, setIsFullWidth);
       const legendcontrol = new ColorPalette1();
        // Add the control to the map
        map.addControl(legendcontrol, 'bottom-left');

        // Store the new control instance to track it for future updates
        setLegendControl(legendcontrol);
    }
}, [activeMetric, map]); // Re-run this effect when activeMetric changes

class MetricToggleButton {
  constructor(setActiveMetric, activeMetric) {
      this.container = document.createElement('div');
      this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-top';
      
      // Styling for the container
      Object.assign(this.container.style, {
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)', // Center horizontally
          zIndex: '10',
          backgroundColor: '#00000061',
          padding: '5px 10px',
          borderRadius: '30px',
          display: 'flex',
          gap: '10px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.3)', // Adds a slight shadow
          fontSize:'12px'
      });

      // ROI Button
      this.roiButton = document.createElement('button');
      this.roiButton.textContent = "ROI";
      this.roiButton.onclick = () => setActiveMetric('roi');
      this.styleButton(this.roiButton, activeMetric === 'roi');

      // Appreciation Button
      this.appreciationButton = document.createElement('button');
      this.appreciationButton.textContent = "Appreciation";
      this.appreciationButton.onclick = () => setActiveMetric('appreciation');
      this.styleButton(this.appreciationButton, activeMetric === 'appreciation');
      
      // Score Button
      this.scoreButton = document.createElement('button');
      this.scoreButton.textContent = "FLI";
      this.scoreButton.onclick = () => setActiveMetric('score');
      this.styleButton(this.scoreButton, activeMetric === 'score');

      // Append buttons to container
      this.container.appendChild(this.roiButton);
      this.container.appendChild(this.appreciationButton);
      this.container.appendChild(this.scoreButton);
  }

  // Apply styles to buttons and highlight the active one
  styleButton(button, isActive) {
      Object.assign(button.style, {
          padding: '8px 15px',
          border: 'none',
          borderRadius: '20px',
          cursor: 'pointer',
          fontWeight: 'bold',
          background: isActive ? '#ffffff' : 'none',
          color: isActive ? 'black' : 'white'
      });
  }

  // When control is added to the map
  onAdd(map) {
      return this.container;
  }

  // When control is removed
  onRemove() {
      this.container.parentNode.removeChild(this.container);
  }

  // Update button styles when metric changes
  updateButtonText(activeMetric) {
      this.styleButton(this.roiButton, activeMetric === 'roi');
      this.styleButton(this.appreciationButton, activeMetric === 'appreciation');
      this.styleButton(this.scoreButton, activeMetric === 'score');
  }
}

class ColorPalette1 {
  constructor() {
      this.container = document.createElement('div');
      this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group color-palatte-outer';
      this.container.style.bottom = '10px';
      this.container.style.left = '10px';
      this.updateContent();
  }

  updateContent() {
      this.container.innerHTML = '';
      const colorLegendContainer = document.createElement('div');
      colorLegendContainer.style.display = 'flex';
      
      roiColors[activeMetric].map((item) => {
          const colorBlock = document.createElement('div');
          colorBlock.className = 'color-palatte-single';
          colorBlock.style.backgroundColor = item.color;

          const label = document.createElement('div');
          //label.textContent = item.label + '+';
          label.textContent = item.label === 'All' ? item.label : `${item.label}+`;

          colorBlock.appendChild(label);

          colorBlock.addEventListener('click', () => {
              console.log("selected lable", item.label);
              if(activeMetric == 'roi'){
                  setRoiFilter(parseInt(item.label, 10));
                  setAppreciationFilter('All');
                  setScoreFilter('All');
              }
              else if(activeMetric == 'appreciation'){
                  //setAppreciationFilter(parseInt(item.label, 10));
                  setAppreciationFilter(item.label === 'All' ? 'All' : parseInt(item.label, 10));
                  setRoiFilter('All');
                  setScoreFilter('All');
              }
              else if(activeMetric == 'score'){
                  setScoreFilter(parseInt(item.label, 10));
                  setRoiFilter('All');
                  setAppreciationFilter('All');
                  
              }
          });
          colorLegendContainer.appendChild(colorBlock);
      });

      this.container.appendChild(colorLegendContainer);

    
  }

  onAdd() {
      return this.container;
  }

  onRemove() {
      this.container.parentNode.removeChild(this.container);
  }
}




  const fetchFeatureData = () => {
    const map = mapRef.current;
    if (!map) return;

    const source = map.getSource('vector-tiles');
    if (!source || !map.isSourceLoaded('vector-tiles')) return;

    const features = map.queryRenderedFeatures({ layers: ["point-layer"] });
    const seen = new Set();
    const uniqueData = [];

    for (const feature of features) {
      const zipcode = feature.properties.zipcode;
      const [lon, lat] = feature.geometry.coordinates;
      if (!seen.has(zipcode)) {
        seen.add(zipcode);
        uniqueData.push({
          zipcode,
          roi: feature.properties.roi,
          latitude: lat,
          longitude: lon,
          appr: feature.properties.appreciation,
          score: feature.properties.score,
          price: feature.properties.sales,
          state: feature.properties.state,
          city: feature.properties.city,
        });
      }
    }
   
    setOriginalPropertyList(uniqueData); // Store the original list of properties
    //setPropertyList(uniqueData); // Initially, show all properties
  };
  

  return (
    <div className="point-component-container">
      <div className="listOuter">
        <h3 className="property-list-heading">
          <div className="tab-navigation">
            <button onClick={() => setActiveTab('all')} className={activeTab === 'all' ? 'active' : ''}>All List</button>
            <button onClick={() => setActiveTab('favorites')} className={activeTab === 'favorites' ? 'active' : ''}>Favorite Properties</button>
          </div>
        </h3>

        <div className="search">
          <div className="geoCodeSerach" ref={geocoderContainerRef} />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '6px', fontSize: '1rem' }}
          />
        </div>

        <PaginatedTable
          featureData={propertyList}
          activeTab={activeTab}
          favoriteZipcodes={favoriteZipcodes}
          searchTerm={searchTerm}
        />
      </div>

      <div ref={mapContainer} style={{ width: '60%', height: '100%' }} />
    </div>
  );
};

export default PointComponent;